import type { CreateProjectFromMatchResult } from "./use-match-editor";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl, type IntlShape } from "react-intl";
import { userErrorMessage } from "@/lib/user-error";
import { useQuery } from "@apollo/client/react";
import { Button } from "@/components/design/Button";
import {
  LinkableSharedItemsDocument,
  MatchPersonSearchDocument,
  SortOrder,
  StringFilterCaseSensitivity,
} from "@/graphql/generated/operations";
import type { MatchData } from "./match-design-types";
import {
  UNKNOWN_MATCH_MODE,
  assertUnhandledMode,
  resolveMatchMode,
  type UiMatchMode,
} from "./match-mode";
import type { UseMatchEditorResult } from "./use-match-editor";
import { ModeSelect } from "./ModeSelect";
import type { ResultSectionLabel } from "./result-sections";
import { ResultSectionPicker } from "./ResultSectionPicker";
import {
  axesToRole,
  roleToAxes,
  ROLE_HINT_KEYS,
  ROLE_LABEL_KEYS,
} from "./match-access";
import type { AccessRowDraft, AssignableRole } from "./match-access";

function hasOverlapContent(data: MatchData): boolean {
  return (
    data.projectName.trim().length > 0 ||
    data.projectShortDescription.trim().length > 0 ||
    data.projectDescription.trim().length > 0 ||
    data.projectFiles.length > 0
  );
}

export function MatchSettingsModal({
  editor,
  onClose,
}: {
  editor: UseMatchEditorResult;
  onClose: () => void;
}) {
  const intl = useIntl();
  const matchData = editor.data;
  const canEdit = editor.actions?.canManageAccess === true;
  const owner = editor.owner;

  const [matchTitle, setMatchTitle] = useState(matchData.matchTitle);
  const [matchDescription, setMatchDescription] = useState(matchData.matchDescription);
  const [access, setAccess] = useState<AccessRowDraft[]>(() =>
    editor.access
      .filter((row) => row.person.id !== owner?.id)
      .map((row) => ({
        personId: row.person.id,
        formattedName: row.person.formattedName ?? "?",
        role: axesToRole({
          prepare: row.prepare,
          design: row.design,
          discover: row.discover,
          match: row.match,
        }),
      })),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const busy = saving;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  function setRowRole(personId: string, nextRole: AssignableRole) {
    setAccess((rows) =>
      rows.map((r) => (r.personId === personId ? { ...r, role: nextRole } : r)),
    );
  }
  function removeRow(personId: string) {
    setAccess((rows) => rows.filter((r) => r.personId !== personId));
  }
  function addPerson(p: { id: string; formattedName: string }) {
    setAccess((rows) =>
      rows.some((r) => r.personId === p.id)
        ? rows
        : [...rows, { personId: p.id, formattedName: p.formattedName, role: "editor" }],
    );
  }

  async function save() {
    if (busy || !canEdit) return;
    setSaving(true);
    setError(null);
    try {
      await editor.saveSettings({
        matchTitle: matchTitle.trim(),
        matchDescription,
        access: access.map((r) => ({ personId: r.personId, ...roleToAxes(r.role) })),
      });
      onClose();
    } catch (e) {
      setError(userErrorMessage(intl, e));
    } finally {
      setSaving(false);
    }
  }

  const excludeIds = useMemo(
    () => [owner?.id, ...access.map((r) => r.personId)].filter((s): s is string => !!s),
    [access, owner?.id],
  );

  return (
    <div id="match-settings-modal" className="fixed inset-0 z-50 flex items-center justify-center p-6" onClick={() => !busy && onClose()}>
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" />
      <div
        className="box relative flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden bg-surface"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-ink/5 px-6 py-4">
          <div className="text-smycles-micro tracking-[0.25em] text-ink-muted">
            <FormattedMessage id="match.settingsEyebrow" defaultMessage="Match-Einstellungen" />
          </div>
          <div className="mt-0.5 font-serif text-smycles-display-s">
            <FormattedMessage id="match.modal.nameNotesAccess" defaultMessage="Name, Notizen & Zugriff" />
          </div>
        </div>

        <div className="flex-1 space-y-5 overflow-auto px-6 py-5">
          {!canEdit && (
            <div className="bg-ink/[0.06] rounded-md px-3 py-2 text-smycles-meta text-ink-muted">
              <FormattedMessage
                id="match.modal.noWriteRights"
                defaultMessage="Du hast keine Schreibrechte auf diese Match-Einstellungen."
              />
            </div>
          )}

          <div>
            <div className="mb-2 text-smycles-micro tracking-[0.25em] text-ink-muted">
              <FormattedMessage
                id="match.modePicker.title"
                defaultMessage="What question should this match answer?"
              />
            </div>
            {/* Mode is immutable after creation (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@4): always read-only
                here; it is chosen once in NewMatchModal at creation. */}
            <ModeSelect
              idPrefix="match-settings-mode"
              value={resolveMatchMode(matchData.matchMode)}
              onChange={() => {}}
              disabled
            />
          </div>

          <div>
            <div className="mb-1 text-smycles-micro tracking-[0.25em] text-ink-muted">
              <FormattedMessage id="match.matchNameLabel" defaultMessage="Match-Name" />
            </div>
            <input
              id="match-settings-name"
              value={matchTitle}
              onChange={(e) => setMatchTitle(e.target.value)}
              disabled={!canEdit}
              placeholder={intl.formatMessage({ id: "match.titlePlaceholder", defaultMessage: "z. B. Set-Crew BTL · Q2 2026" })}
              className="bg-page w-full rounded-md px-3 py-2 text-smycles-body ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui disabled:opacity-60"
            />
          </div>

          <div>
            <div className="mb-1 text-smycles-micro tracking-[0.25em] text-ink-muted">
              <FormattedMessage id="match.modal.matchNotes" defaultMessage="Notizen zum Match" />
            </div>
            <textarea
              id="match-settings-notes"
              value={matchDescription}
              onChange={(e) => setMatchDescription(e.target.value)}
              disabled={!canEdit}
              rows={3}
              placeholder={intl.formatMessage({
                id: "match.modal.matchNotesPlaceholder",
                defaultMessage: "Interne Notizen, Kontext oder offene Fragen – nicht die Projektbeschreibung.",
              })}
              className="bg-page w-full resize-none rounded-md px-3 py-2 text-smycles-body leading-relaxed ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui disabled:opacity-60"
            />
          </div>

          <div>
            <div className="mb-2 text-smycles-micro tracking-[0.25em] text-ink-muted">
              <FormattedMessage
                id="match.modal.accessCount"
                defaultMessage="Zugriff · {count}"
                values={{ count: access.length + (owner ? 1 : 0) }}
              />
            </div>

            <div className="bg-page box-flat divide-y divide-ink/5 px-4">
              {owner && <OwnerAccessRow name={owner.formattedName ?? "?"} />}
              {access.map((row) => (
                <AccessRow
                  key={row.personId}
                  row={row}
                  canEdit={canEdit}
                  onChange={(nextRole) => setRowRole(row.personId, nextRole)}
                  onRemove={() => removeRow(row.personId)}
                />
              ))}
              {access.length === 0 && !owner && (
                <div className="py-6 text-center text-smycles-meta text-ink-muted">
                  <FormattedMessage
                    id="match.modal.noAccessYet"
                    defaultMessage="Niemand hat Zugriff. Unten Person hinzufügen."
                  />
                </div>
              )}
            </div>

            {canEdit && (
              <div className="mt-3">
                <PersonAdder idPrefix="match-settings-access" onAdd={addPerson} excludeIds={excludeIds} />
              </div>
            )}
          </div>

          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-smycles-meta text-red-700">{error}</div>
          )}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-ink/5 px-6 py-4">
          <Button id="match-settings-cancel" variant="secondary" size="compact" onClick={onClose} disabled={busy}>
            {canEdit ? (
              <FormattedMessage id="common.cancel" defaultMessage="Abbrechen" />
            ) : (
              <FormattedMessage id="common.close" defaultMessage="Schließen" />
            )}
          </Button>
          {canEdit && (
            <Button
              id="match-settings-save"
              variant="primary"
              size="compact"
              onClick={() => void save()}
              disabled={busy}
            >
              {saving ? (
                <FormattedMessage id="match.modal.savingEllipsis" defaultMessage="Speichert …" />
              ) : (
                <FormattedMessage id="common.save" defaultMessage="Speichern" />
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * The owner's fixed line at the top of an access list: outside the editable rows, because ownership
 * comes from createdBy, not from the list. Shared by the match and the project settings so the two
 * cannot drift apart (spark#1001).
 */
export function OwnerAccessRow({ name }: { name: string }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <div className="min-w-0">
        <div className="truncate text-smycles-meta font-medium">{name}</div>
        <div className="text-smycles-micro tracking-[0.2em] text-ink-muted">
          <FormattedMessage id="match.modal.ownerCreatedBy" defaultMessage="Eigentümer:in · erstellt von" />
        </div>
      </div>
      <div className="text-smycles-label text-ink-muted">
        <FormattedMessage id="match.modal.owner" defaultMessage="Eigentümer:in" />
      </div>
    </div>
  );
}

export function AccessRow({
  row,
  canEdit,
  onChange,
  onRemove,
}: {
  row: AccessRowDraft;
  canEdit: boolean;
  onChange: (role: AssignableRole) => void;
  onRemove: () => void;
}) {
  const intl = useIntl();
  return (
    <div
      id={`match-access-row-${row.personId}`}
      className="flex items-center gap-3 py-2"
    >
      <div className="min-w-0 flex-1 truncate text-smycles-meta font-medium">{row.formattedName}</div>
      <select
        id={`match-access-role-${row.personId}`}
        value={row.role}
        disabled={!canEdit}
        onChange={(e) => onChange(e.target.value as AssignableRole)}
        className="bg-page rounded px-2 py-1 text-smycles-meta ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui disabled:opacity-60"
        title={intl.formatMessage(ROLE_HINT_KEYS[row.role])}
      >
        <option value="admin">{intl.formatMessage(ROLE_LABEL_KEYS.admin)}</option>
        <option value="editor">{intl.formatMessage(ROLE_LABEL_KEYS.editor)}</option>
        <option value="reader">{intl.formatMessage(ROLE_LABEL_KEYS.reader)}</option>
      </select>
      {canEdit && (
        <Button
          id={`match-access-remove-${row.personId}`}
          variant="icon"
          size="sm"
          onClick={onRemove}
          title={intl.formatMessage({ id: "common.remove", defaultMessage: "Entfernen" })}
          aria-label={intl.formatMessage({ id: "common.remove", defaultMessage: "Entfernen" })}
        >
          ✕
        </Button>
      )}
    </div>
  );
}

export function PersonAdder({
  onAdd,
  excludeIds,
  idPrefix,
}: {
  onAdd: (p: { id: string; formattedName: string }) => void;
  excludeIds: string[];
  // Zwei Dialoge zeigen denselben Sucher; die Kennung braucht den Dialog davor,
  // sonst wären beide gleich benannt (UI_CONVENTIONS → Test anchors).
  idPrefix: string;
}) {
  const intl = useIntl();
  const [query, setQuery] = useState("");
  const trimmed = query.trim();
  const { data, loading } = useQuery(MatchPersonSearchDocument, {
    variables: {
      input: {
        first: 10,
        where:
          trimmed.length >= 2
            ? { formattedName: { contains: trimmed, caseSensitivity: StringFilterCaseSensitivity.CaseInsensitive } }
            : undefined,
      },
    },
    skip: trimmed.length < 2,
    fetchPolicy: "cache-and-network",
  });

  const results = (data?.persons.nodes ?? []).filter(
    (p) => !excludeIds.includes(p.id),
  );

  return (
    <div>
      <input
        id={`${idPrefix}-search`}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={intl.formatMessage({
          id: "match.modal.addPersonPlaceholder",
          defaultMessage: "+ Person hinzufügen – Name suchen …",
        })}
        className="bg-page w-full rounded-md px-3 py-2 text-smycles-body ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui"
      />
      {trimmed.length >= 2 && (
        <div className="bg-page box-flat mt-2 max-h-48 overflow-auto">
          {loading && results.length === 0 ? (
            <div className="px-3 py-4 text-center text-smycles-meta text-ink-muted">
              <FormattedMessage id="match.modal.searchingEllipsis" defaultMessage="sucht …" />
            </div>
          ) : results.length === 0 ? (
            <div className="px-3 py-4 text-center text-smycles-meta text-ink-muted">
              <FormattedMessage id="match.modal.noHits" defaultMessage="Keine Treffer." />
            </div>
          ) : (
            <ul className="divide-y divide-ink/5">
              {results.map((p) => (
                <li key={p.id}>
                  <button
                    id={`${idPrefix}-hit-${p.id}`}
                    onClick={() => {
                      onAdd({ id: p.id, formattedName: p.formattedName ?? "?" });
                      setQuery("");
                    }}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-smycles-meta hover:bg-ink/[0.06]"
                  >
                    <span className="truncate">{p.formattedName ?? "?"}</span>
                    {p.profession && (
                      <span className="shrink-0 text-smycles-micro text-ink-muted">{p.profession}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export interface SaveMatchPreviewMeta {
  /**
   * null where the mode has no team to size — a company ranking judges a pool of companies and
   * composes nothing (see match-mode's `showsTeamControls`). The preview renders "–" for it, which
   * is what an absent value should read as; 0 would be a number nobody chose.
   */
  teamSize: number | null;
  candidateCount: number | null;
  requirementsCount: number;
  agenticAi: boolean;
  // Resolved, not raw: the preview labels the mode, and the label must not fall back to a mode
  // it isn't (see saveModeLabel below).
  matchMode: UiMatchMode;
}

export interface SaveMatchPreviewPerson {
  id: string;
  name: string;
  profession?: string | null;
}

// Exhaustive on the mode: the cascade this replaced ended in "Optimieren", so a mode this build
// cannot name labelled its own saved result as an Optimize run. "–" is the same placeholder the
// neighbouring stats use for a value they don't have.
function saveModeLabel(intl: IntlShape, mode: UiMatchMode): string {
  switch (mode) {
    case "ASSESS":
      return intl.formatMessage({ id: "match.save.modeAssess", defaultMessage: "Bewerten" });
    case "RANK":
      return intl.formatMessage({ id: "match.save.modeRank", defaultMessage: "Rangliste" });
    case "OPTIMIZE":
      return intl.formatMessage({ id: "match.save.modeOptimize", defaultMessage: "Optimieren" });
    // Named by its card title, like the workflow pill and for the same reason: this mode and Rank
    // deliberately share the result word, so the output cannot tell them apart.
    case "RANK_COMPANIES":
      return intl.formatMessage({
        id: "match.save.modeRankCompanies",
        defaultMessage: "Unternehmen vergleichen",
      });
    case UNKNOWN_MATCH_MODE:
      return "–";
    default:
      return assertUnhandledMode(mode);
  }
}

function PreviewStat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="smycles-field-label">{label}</div>
      <div className="mt-0.5 text-smycles-meta font-medium text-ink tabular-nums">{value}</div>
    </div>
  );
}

export function SaveMatchModal({
  defaultName,
  defaultNotes,
  onSave,
  onClose,
  previewPersons = [],
  previewMeta,
  sectionChoice,
}: {
  defaultName: string;
  defaultNotes: string;
  onSave: (input: { name: string; notes?: string; hiddenSections?: string[] }) => Promise<void>;
  onClose: () => void;
  previewPersons?: SaveMatchPreviewPerson[];
  previewMeta?: SaveMatchPreviewMeta;
  /**
   * Welche Abschnitte das Ergebnis zeigen soll. Fehlt die Liste, blendet der Dialog die Auswahl
   * aus — das Unternehmens-Ranking reicht ihre eigene herein, ein Aufrufer ohne Abschnitte bekommt
   * keinen leeren Kasten.
   */
  sectionChoice?: {
    sectionIds: readonly string[];
    labels: Record<string, ResultSectionLabel>;
  };
}) {
  const intl = useIntl();
  const [name, setName] = useState(defaultName || "");
  const [desc, setDesc] = useState(defaultNotes || "");
  const [saving, setSaving] = useState(false);
  // Voreinstellung ist "alles zeigen", also eine leere Liste. Eine Auswahl aus dem vorigen
  // Speichern zu uebernehmen waere ein stiller Zustand: der Nutzer sieht im Dialog eine
  // Vorauswahl, die er nie getroffen hat.
  const [hiddenSections, setHiddenSections] = useState<string[]>([]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  async function save() {
    if (!name.trim() || saving) return;
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        notes: desc.trim() || undefined,
        // Leere Liste statt undefined: "nichts ausgeblendet" ist eine Aussage, kein
        // fehlender Wert. Sonst koennte ein spaeteres Update sie nicht zuruecksetzen.
        ...(sectionChoice ? { hiddenSections } : {}),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div id="save-result-modal" className="fixed inset-0 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" />
      <div
        // max-h + flex-col + der scrollende Mittelteil: dieselbe Huelle wie MatchSettingsModal.
        // Ohne sie wuchs der Dialog mit der Abschnittsliste aus dem Fenster heraus und die
        // Knoepfe am Fuss waren nicht mehr erreichbar.
        className="box relative flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden bg-surface"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-ink/5 px-6 py-4">
          <div className="text-smycles-micro tracking-[0.25em] text-ink-muted">
            <FormattedMessage id="match.save.eyebrow" defaultMessage="Ergebnis speichern" />
          </div>
          <div className="mt-0.5 font-serif text-smycles-display-s">
            <FormattedMessage id="match.save.previewAndNote" defaultMessage="Vorschau & Notiz" />
          </div>
        </div>
        {(previewPersons.length > 0 || previewMeta) && (
          <div className="bg-ink/[0.06] max-h-[30vh] shrink-0 space-y-3 overflow-auto border-b border-ink/5 px-6 py-4">
            {previewPersons.length > 0 && (
              <div>
                <div className="smycles-field-label mb-2">
                  <FormattedMessage
                    id="match.save.teamCount"
                    defaultMessage="Team · {count, plural, one {# Person} other {# Personen}}"
                    values={{ count: previewPersons.length }}
                  />
                </div>
                <ul className="max-h-44 space-y-1 overflow-y-auto pr-1 text-smycles-meta">
                  {previewPersons.map((p) => (
                    <li key={p.id} className="flex items-baseline justify-between gap-3">
                      <span className="truncate text-ink">{p.name}</span>
                      {p.profession ? (
                        <span className="shrink-0 text-smycles-label text-ink-muted">{p.profession}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {previewMeta && (
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-smycles-label @sm:grid-cols-5">
                <PreviewStat
                  label={intl.formatMessage({ id: "match.save.statMode", defaultMessage: "Modus" })}
                  value={saveModeLabel(intl, previewMeta.matchMode)}
                />
                <PreviewStat
                  label={intl.formatMessage({ id: "match.save.statTarget", defaultMessage: "Soll" })}
                  value={previewMeta.teamSize || "–"}
                />
                <PreviewStat
                  label={intl.formatMessage({ id: "match.save.statPool", defaultMessage: "Pool" })}
                  value={previewMeta.candidateCount != null ? previewMeta.candidateCount : "–"}
                />
                <PreviewStat
                  label={intl.formatMessage({ id: "match.save.statConditions", defaultMessage: "Bedingungen" })}
                  value={previewMeta.requirementsCount}
                />
                <PreviewStat
                  label={intl.formatMessage({ id: "match.save.statAgenticAi", defaultMessage: "Agentic AI" })}
                  value={
                    previewMeta.agenticAi
                      ? intl.formatMessage({ id: "match.save.on", defaultMessage: "ein" })
                      : intl.formatMessage({ id: "match.save.off", defaultMessage: "aus" })
                  }
                />
              </div>
            )}
          </div>
        )}
        <div className="flex-1 space-y-5 overflow-auto px-6 py-5">
          <div>
            <div className="mb-1 text-smycles-micro tracking-[0.25em] text-ink-muted">
              <FormattedMessage id="match.save.resultName" defaultMessage="Ergebnis-Name" />
            </div>
            <input
              id="save-result-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              placeholder={intl.formatMessage({ id: "match.titlePlaceholder", defaultMessage: "z. B. Set-Crew BTL · Q2 2026" })}
              className="bg-page w-full rounded-md px-3 py-2 text-smycles-body ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui"
            />
          </div>
          <div>
            <div className="mb-1 text-smycles-micro tracking-[0.25em] text-ink-muted">
              <FormattedMessage id="match.save.note" defaultMessage="Notiz" />
            </div>
            <textarea
              id="save-result-note"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              rows={4}
              placeholder={intl.formatMessage({
                id: "match.save.notePlaceholder",
                defaultMessage: "Optional – Kontext, Annahmen, nächste Schritte …",
              })}
              className="bg-page w-full resize-none rounded-md px-3 py-2 text-smycles-body leading-relaxed ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui"
            />
          </div>
          {sectionChoice && (
            <div>
              <div className="mb-1 text-smycles-micro tracking-[0.25em] text-ink-muted">
                <FormattedMessage
                  id="match.save.sections"
                  defaultMessage="Angezeigte Abschnitte"
                />
              </div>
              <ResultSectionPicker
                sectionIds={sectionChoice.sectionIds}
                labels={sectionChoice.labels}
                hidden={hiddenSections}
                onChange={setHiddenSections}
                disabled={saving}
                variant="plain"
              />
            </div>
          )}
        </div>
        <div className="flex shrink-0 justify-end gap-2 border-t border-ink/5 px-6 py-4">
          <Button
            id="save-result-cancel"
            variant="secondary"
            size="compact"
            onClick={onClose}
            disabled={saving}
          >
            <FormattedMessage id="common.cancel" defaultMessage="Abbrechen" />
          </Button>
          <Button
            id="save-result-save"
            variant="primary"
            size="compact"
            onClick={() => void save()}
            disabled={!name.trim() || saving}
          >
            {saving ? (
              <FormattedMessage id="match.modal.savingEllipsis" defaultMessage="Speichert …" />
            ) : (
              <FormattedMessage id="common.save" defaultMessage="Speichern" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Verknüpfen picker – lists SharedItems the user can see, with their own
// (owner=true) grouped above the rest per @adr SPARK-0006-project-as-conceptual-entity@1. Selection commits via
// one of two paths: silent copy (when the Match has no overlap content) or
// the 3-button overwrite dialog (Übernehmen / Nur verknüpfen / Abbrechen).
export function VerknuepfenModal({
  data,
  canCopy,
  onConfirm,
  onClose,
}: {
  data: MatchData;
  canCopy: boolean;
  onConfirm: (input: { sharedItemId: string; copy: boolean }) => Promise<void>;
  onClose: () => void;
}) {
  const intl = useIntl();
  const [query, setQuery] = useState("");
  const trimmed = query.trim();
  const [selected, setSelected] = useState<{ id: string; name: string | null | undefined } | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasContent = hasOverlapContent(data);

  // Two parallel queries so the user's own items can be visually grouped first
  // without server-side ordering on `owner` (the API supports filtering, not
  // ordering, on derived booleans).
  const ownQuery = useQuery(LinkableSharedItemsDocument, {
    variables: {
      input: {
        first: 30,
        order: { name: SortOrder.Asc },
        where: {
          owner: { eq: true },
          ...(trimmed.length >= 2
            ? {
                name: {
                  contains: trimmed,
                  caseSensitivity: StringFilterCaseSensitivity.CaseInsensitive,
                },
              }
            : {}),
        },
      },
    },
    fetchPolicy: "cache-and-network",
  });
  const restQuery = useQuery(LinkableSharedItemsDocument, {
    variables: {
      input: {
        first: 30,
        order: { name: SortOrder.Asc },
        where: {
          owner: { eq: false },
          ...(trimmed.length >= 2
            ? {
                name: {
                  contains: trimmed,
                  caseSensitivity: StringFilterCaseSensitivity.CaseInsensitive,
                },
              }
            : {}),
        },
      },
    },
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  async function pick(copy: boolean) {
    if (!selected || submitting || (copy && !canCopy)) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm({ sharedItemId: selected.id, copy });
      onClose();
    } catch (e) {
      setError(userErrorMessage(intl, e));
    } finally {
      setSubmitting(false);
    }
  }

  const ownItems = ownQuery.data?.sharedItems.nodes ?? [];
  const restItems = restQuery.data?.sharedItems.nodes ?? [];
  const loading = ownQuery.loading || restQuery.loading;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      onClick={!submitting ? onClose : undefined}
    >
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" />
      <div
        className="box relative flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden bg-surface"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-ink/5 px-6 py-4">
          <div className="text-smycles-micro tracking-[0.25em] text-ink-muted">
            <FormattedMessage id="match.modal.linkWithShareItem" defaultMessage="Mit Share-Item verknüpfen" />
          </div>
          <div className="mt-0.5 font-serif text-smycles-display-s">
            <FormattedMessage id="match.modal.chooseShareItem" defaultMessage="Share-Item wählen" />
          </div>
        </div>

        <div className="flex-1 space-y-4 overflow-auto px-6 py-5">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={intl.formatMessage({
              id: "match.modal.searchByName",
              defaultMessage: "Nach Name suchen …",
            })}
            className="bg-page w-full rounded-md px-3 py-2 text-smycles-body ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui"
            autoFocus
          />

          <SharedItemGroup
            label={
              ownItems.length
                ? intl.formatMessage(
                    { id: "match.modal.myShareItemsCount", defaultMessage: "Meine Share-Items · {count}" },
                    { count: ownItems.length },
                  )
                : intl.formatMessage({ id: "match.modal.myShareItems", defaultMessage: "Meine Share-Items" })
            }
            items={ownItems}
            loading={loading && ownItems.length === 0}
            selectedId={selected?.id ?? null}
            onSelect={(s) => setSelected({ id: s.id, name: s.name })}
          />
          <SharedItemGroup
            label={
              restItems.length
                ? intl.formatMessage(
                    { id: "match.modal.visibleToMeCount", defaultMessage: "Sichtbar für mich · {count}" },
                    { count: restItems.length },
                  )
                : intl.formatMessage({ id: "match.modal.visibleToMe", defaultMessage: "Sichtbar für mich" })
            }
            items={restItems}
            loading={loading && restItems.length === 0}
            selectedId={selected?.id ?? null}
            onSelect={(s) => setSelected({ id: s.id, name: s.name })}
          />
        </div>

        {error && (
          <div className="border-t border-ink/5 px-6 py-3">
            <div className="rounded-md bg-red-50 px-3 py-2 text-smycles-meta text-red-700">{error}</div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-ink/5 px-6 py-4">
          <Button
            variant="secondary"
            size="compact"
            onClick={onClose}
            disabled={submitting}
          >
            <FormattedMessage id="common.cancel" defaultMessage="Abbrechen" />
          </Button>
          {(hasContent || !canCopy) && (
            <Button
              variant="secondary"
              size="compact"
              onClick={() => void pick(false)}
              disabled={!selected || submitting}
              title={intl.formatMessage({
                id: "match.modal.linkOnlyTitle",
                defaultMessage: "Verknüpfen, ohne Inhalte zu kopieren",
              })}
            >
              {submitting ? (
                "…"
              ) : (
                <FormattedMessage id="match.modal.linkOnly" defaultMessage="Nur verknüpfen" />
              )}
            </Button>
          )}
          {canCopy && <Button
            variant="primary"
            size="compact"
            onClick={() => void pick(true)}
            disabled={!selected || submitting}
            title={
              hasContent
                ? intl.formatMessage({
                    id: "match.modal.overwriteTitle",
                    defaultMessage: "Projekt-Inhalte mit dem Share-Item überschreiben",
                  })
                : intl.formatMessage({
                    id: "match.modal.fillTitle",
                    defaultMessage: "Projekt mit dem Share-Item füllen",
                  })
            }
          >
            {submitting ? (
              <FormattedMessage id="match.modal.linking" defaultMessage="Verknüpft …" />
            ) : hasContent ? (
              <FormattedMessage id="match.modal.takeOver" defaultMessage="Übernehmen" />
            ) : (
              <FormattedMessage id="match.modal.link" defaultMessage="Verknüpfen" />
            )}
          </Button>}
        </div>
      </div>
    </div>
  );
}

function SharedItemGroup({
  label,
  items,
  loading,
  selectedId,
  onSelect,
}: {
  label: string;
  items: ReadonlyArray<{
    id: string;
    name?: string | null;
    shortDescription?: string | null;
    owner?: boolean | null;
    createdBy?: { id: string; formattedName?: string | null } | null;
  }>;
  loading: boolean;
  selectedId: string | null;
  onSelect: (item: { id: string; name: string | null | undefined }) => void;
}) {
  if (!loading && items.length === 0) return null;
  return (
    <div>
      <div className="mb-1 text-smycles-micro tracking-[0.25em] text-ink-muted">{label}</div>
      {loading && items.length === 0 ? (
        <div className="px-3 py-4 text-center text-smycles-meta text-ink-muted">
          <FormattedMessage id="match.modal.loadingEllipsis" defaultMessage="lädt …" />
        </div>
      ) : (
        <ul className="bg-page box-flat divide-y divide-ink/5">
          {items.map((s) => {
            const active = s.id === selectedId;
            return (
              <li key={s.id}>
                <button
                  aria-pressed={active}
                  onClick={() => onSelect({ id: s.id, name: s.name })}
                  className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left transition ${
                    active ? "bg-ink/[0.06] smycles-selected ring-2 ring-accent-decoration" : "hover:bg-ink/[0.06]"
                  }`}
                >
                  <div className="min-w-0">
                    <div className="truncate text-smycles-meta font-medium">
                      {s.name || (
                        <FormattedMessage
                          id="match.modal.unnamedShareItem"
                          defaultMessage="Unbenanntes Share-Item"
                        />
                      )}
                    </div>
                    {s.shortDescription && (
                      <div className="truncate text-smycles-label text-ink-muted">{s.shortDescription}</div>
                    )}
                  </div>
                  <div className="shrink-0 text-smycles-micro tracking-wider text-ink-muted">
                    {s.createdBy?.formattedName ?? ""}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// Confirm dialog for Verbindung lösen. Per @adr SPARK-0006-project-as-conceptual-entity@1 the copy-not-sync model
// means content stays – unlinking only clears Match.sharedItem + linkedAt.
export function UnlinkConfirmModal({
  sharedItemName,
  onConfirm,
  onClose,
}: {
  sharedItemName: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}) {
  const intl = useIntl();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  async function confirm() {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (e) {
      setError(userErrorMessage(intl, e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      onClick={!submitting ? onClose : undefined}
    >
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" />
      <div
        className="box relative w-full max-w-md overflow-hidden bg-surface"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-ink/5 px-6 py-4">
          <div className="text-smycles-micro tracking-[0.25em] text-ink-muted">
            <FormattedMessage id="match.modal.unlinkEyebrow" defaultMessage="Verbindung lösen" />
          </div>
          <div className="mt-0.5 font-serif text-smycles-display-s">
            <FormattedMessage id="match.modal.removeLink" defaultMessage="Verknüpfung entfernen?" />
          </div>
        </div>
        <div className="space-y-3 px-6 py-5 text-smycles-meta text-ink">
          <p>
            <FormattedMessage
              id="match.modal.unlinkBody"
              defaultMessage="Die Verknüpfung zu <b>{name}</b> wird entfernt."
              values={{
                name: sharedItemName,
                b: (chunks) => <span className="font-medium">{chunks}</span>,
              }}
            />
          </p>
          <p className="text-smycles-meta text-ink-muted">
            <FormattedMessage
              id="match.modal.unlinkHint"
              defaultMessage="Die kopierten Inhalte (Titel, Beschreibung, Dokumente) bleiben erhalten – nur der Hinweis auf das Share-Item verschwindet."
            />
          </p>
          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-smycles-meta text-red-700">{error}</div>
          )}
        </div>
        <div className="flex shrink-0 justify-end gap-2 border-t border-ink/5 px-6 py-4">
          <Button
            variant="secondary"
            size="compact"
            onClick={onClose}
            disabled={submitting}
          >
            <FormattedMessage id="common.cancel" defaultMessage="Abbrechen" />
          </Button>
          <Button
            variant="primary"
            size="compact"
            onClick={() => void confirm()}
            disabled={submitting}
          >
            {submitting ? (
              <FormattedMessage id="match.modal.unlinking" defaultMessage="Trennt …" />
            ) : (
              <FormattedMessage id="match.modal.unlinkAction" defaultMessage="Verbindung lösen" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function CreateProjectModal({
  projectName,
  onCreate,
  onOpen,
  onClose,
}: {
  projectName: string;
  onCreate: () => Promise<CreateProjectFromMatchResult>;
  onOpen: (projectId: string) => void;
  onClose: () => void;
}) {
  const intl = useIntl();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [partial, setPartial] = useState<CreateProjectFromMatchResult | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  async function run() {
    if (submitting || partial) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await onCreate();
      if (result.documentsError || result.linkError) setPartial(result);
      else onOpen(result.projectId);
    } catch (e) {
      setError(userErrorMessage(intl, e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      onClick={!submitting ? onClose : undefined}
    >
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" />
      <div
        id="match-create-project-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="match-create-project-heading"
        className="box relative w-full max-w-md overflow-hidden bg-surface"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-ink/5 px-6 py-4">
          <div id="match-create-project-heading" className="text-smycles-micro tracking-[0.25em] text-ink-muted">
            <FormattedMessage id="match.modal.publishEyebrow" defaultMessage="Als Projekt anlegen" />
          </div>
          <div className="mt-0.5 font-serif text-smycles-display-s">
            {projectName || intl.formatMessage({ id: "match.unnamedProject", defaultMessage: "Unbenanntes Projekt" })}
          </div>
        </div>
        <div className="space-y-3 px-6 py-5 text-smycles-meta text-ink">
          {!partial && (
            <>
              <p>
                <FormattedMessage
                  id="match.modal.publishQuestion"
                  defaultMessage="Dieses Match als neues Projekt anlegen?"
                />
              </p>
              <p className="text-smycles-meta text-ink-muted">
                <FormattedMessage
                  id="match.modal.publishHint"
                  defaultMessage="Beschreibung und Dokumente werden kopiert. Das Projekt wird mit diesem Match verknüpft und anschließend geöffnet."
                />
              </p>
            </>
          )}
          {error && (
            <div role="alert" className="rounded-md bg-red-50 px-3 py-2 text-smycles-meta text-red-700">{error}</div>
          )}
          {partial && (
            <div role="alert" className="space-y-3">
              {partial.documentsError && <p><FormattedMessage
                id="match.project.documentsFailed"
                defaultMessage="Das Projekt ist angelegt. Nicht alle Dokumente konnten übernommen werden. Du kannst fehlende Dokumente im Projekt ergänzen. {message}"
                values={{ message: partial.documentsError }}
              /></p>}
              {partial.linkError && <p><FormattedMessage
                id="match.project.linkFailed"
                defaultMessage="Das Projekt ist angelegt, konnte aber nicht mit diesem Match verknüpft werden. {message}"
                values={{ message: partial.linkError }}
              /></p>}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-ink/5 px-6 py-4">
          <Button variant="secondary" size="compact" onClick={onClose} disabled={submitting}>
            {partial
              ? <FormattedMessage id="common.close" defaultMessage="Schließen" />
              : <FormattedMessage id="common.cancel" defaultMessage="Abbrechen" />}
          </Button>
          {partial ? (
            <Button variant="primary" size="compact" onClick={() => onOpen(partial.projectId)}>
              <FormattedMessage id="match.project.open" defaultMessage="Projekt öffnen" />
            </Button>
          ) : (
            <Button id="match-create-project-confirm" variant="primary" size="compact" onClick={() => void run()} disabled={submitting}>
              {submitting
                ? intl.formatMessage({ id: "match.modal.publishPublishing", defaultMessage: "Wird angelegt …" })
                : intl.formatMessage({ id: "match.modal.publishNow", defaultMessage: "Als Projekt anlegen" })}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
